import React from 'react';
import { PrintDocumentTitle } from '@/components/shared/print';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { formatNumber } from '@/lib/utils';
import type { ProcessOrder, ProcessMachine } from '@/stores/useProcessPlanningStore';

interface ProcessPlanningPrintContentProps {
    data: ProcessOrder[] | ProcessMachine[];
    viewMode: 'order' | 'machine';
    periodLabel?: string;
    statusLabel?: string;
}

export function ProcessPlanningPrintContent({ data, viewMode, periodLabel, statusLabel }: ProcessPlanningPrintContentProps) {
    const printDate = new Date();
    
    return (
        <div className="p-[10mm] w-full bg-white text-black text-[11px] leading-tight print:exact-colors print:m-0 mx-auto min-h-[210mm] kh-print-doc" id="print-area">
            {/* 3.1 Company Header & ISO Badge */}
            <div className="flex items-start justify-between mb-6">
                <div className="flex gap-4 items-center">
                    {/* Placeholder Logo */}
                    <div className="w-16 h-16 border-2 border-slate-800 flex items-center justify-center font-bold text-xl rounded">
                        APV
                    </div>
                    <div>
                        <h2 className="font-bold text-lg uppercase">CÔNG TY CỔ PHẦN APV</h2>
                        <p className="text-sm">Lô A, KCN Tân Bình, P.Tây Thạnh, Q.Tân Phú, TP.HCM</p>
                    </div>
                </div>
                
            </div>

            {/* 3.2 Title (CENTERED, NO QR) */}
            <PrintDocumentTitle
                title="KẾ HOẠCH SẢN XUẤT"
                subtitle="Production Process Planning"
                date={format(printDate, 'dd/MM/yyyy HH:mm', { locale: vi })}
                dateLabel="Ngày in"
                extraMeta={
                    <span>
                        Kỳ báo cáo: <strong className="text-black">{periodLabel || 'Tất cả'}</strong> • Phân loại: <strong className="text-black">{viewMode === 'order' ? 'Theo Lệnh' : 'Theo Máy'}</strong>
                    </span>
                }
            />


            {/* 3.3 Data Table */}
            <table className="w-full border-collapse border border-slate-800 text-[10pt] mb-8 print:exact-colors">
                <thead>
                    <tr className="bg-slate-100">
                        <th className="border border-slate-800 py-2 px-3 text-center w-10">STT</th>
                        {viewMode === 'order' ? (
                            <>
                                <th className="border border-slate-800 py-2 px-3 text-left w-32">Mã Lệnh</th>
                                <th className="border border-slate-800 py-2 px-3 text-left">Sản phẩm</th>
                                <th className="border border-slate-800 py-2 px-3 text-right w-24">Số lượng</th>
                                <th className="border border-slate-800 py-2 px-3 text-center w-28">Trạng thái</th>
                                <th className="border border-slate-800 py-2 px-3 text-left">Tiến độ công đoạn</th>
                            </>
                        ) : (
                            <>
                                <th className="border border-slate-800 py-2 px-3 text-left w-32">Máy / Chuyền</th>
                                <th className="border border-slate-800 py-2 px-3 text-left w-24">Loại</th>
                                <th className="border border-slate-800 py-2 px-3 text-center w-24">Trạng thái</th>
                                <th className="border border-slate-800 py-2 px-3 text-left">Kế hoạch chạy máy</th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td colSpan={viewMode === 'order' ? 6 : 5} className="border border-slate-800 py-6 px-3 text-center text-slate-500 italic">
                                Không có dữ liệu kế hoạch
                            </td>
                        </tr>
                    ) : (
                        viewMode === 'order' ? (
                            (data as ProcessOrder[]).map((order, index) => (
                                <tr key={order.id} className="print:break-inside-avoid hover:bg-slate-50">
                                    <td className="border border-slate-800 py-2 px-3 text-center">{index + 1}</td>
                                    <td className="border border-slate-800 py-2 px-3 font-medium text-indigo-700">{order.orderId}</td>
                                    <td className="border border-slate-800 py-2 px-3 font-medium">{order.productName}</td>
                                    <td className="border border-slate-800 py-2 px-3 text-right">{formatNumber(order.qtyPlan)}</td>
                                    <td className="border border-slate-800 py-2 px-3 text-center text-[9pt]">{order.status}</td>
                                    <td className="border border-slate-800 py-2 px-3">
                                        <div className="flex flex-col gap-1">
                                            {order.routingSteps?.map(step => (
                                                <div key={step.id} className="flex justify-between items-center text-[9pt] border-b border-dotted border-slate-300 pb-1 last:border-0 last:pb-0">
                                                    <span className="font-medium">{step.processName || step.type}</span>
                                                    <span>{step.resource?.name || '-'}</span>
                                                    <span className="w-16 text-right">[{step.status}]</span>
                                                </div>
                                            ))}
                                            {(!order.routingSteps || order.routingSteps.length === 0) && (
                                                <span className="text-slate-400 italic">Chưa có công đoạn</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            (data as ProcessMachine[]).map((machine, index) => (
                                <tr key={machine.id} className="print:break-inside-avoid hover:bg-slate-50">
                                    <td className="border border-slate-800 py-2 px-3 text-center">{index + 1}</td>
                                    <td className="border border-slate-800 py-2 px-3 font-medium text-indigo-700">{machine.name}</td>
                                    <td className="border border-slate-800 py-2 px-3">{machine.type}</td>
                                    <td className="border border-slate-800 py-2 px-3 text-center text-[9pt]">{machine.status}</td>
                                    <td className="border border-slate-800 py-2 px-3">
                                        <div className="flex flex-col gap-1">
                                            {machine.assignedSteps?.map(step => (
                                                <div key={step.id} className="flex justify-between items-center text-[9pt] border-b border-dotted border-slate-300 pb-1 last:border-0 last:pb-0">
                                                    <span className="font-medium w-24 truncate">{step.productionOrder?.orderId || step.productionOrderId}</span>
                                                    <span className="truncate w-32">{step.productionOrder?.productName || '-'}</span>
                                                    <span className="font-bold">{formatNumber(step.qtyPlan || 0)}</span>
                                                    <span className="w-16 text-right">[{step.status}]</span>
                                                </div>
                                            ))}
                                            {(!machine.assignedSteps || machine.assignedSteps.length === 0) && (
                                                <span className="text-slate-400 italic">Máy đang trống lịch</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )
                    )}
                </tbody>
            </table>

            {/* 3.4 Khối Chữ ký */}
            <div className="grid grid-cols-4 gap-4 text-center mt-8 print:break-inside-avoid">
                <div>
                    <div className="font-bold text-[11pt]">Người lập biểu</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
                <div>
                    <div className="font-bold text-[11pt]">Quản đốc Xưởng</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
                <div>
                    <div className="font-bold text-[11pt]">Trưởng bộ phận</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
                <div>
                    <div className="font-bold text-[11pt]">Giám đốc</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
            </div>
        </div>
    );
}
